/**
 * StatusBadge Component
 * Displays transaction status (new, duplicate, needs review)
 */
import { cn } from '@/utils/cn';
import type { ObservationMatchStatus } from '@/types/document';

export interface StatusBadgeProps {
  status: ObservationMatchStatus;
  confidence?: number;
  className?: string;
}

export function StatusBadge({ status, confidence, className }: StatusBadgeProps) {
  const config = getStatusConfig(status, confidence);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium',
        config.bgColor,
        config.textColor,
        className
      )}
      title={config.tooltip}
    >
      <span className={cn('w-1.5 h-1.5 rounded-full', config.dotColor)} aria-hidden="true" />
      <span>{config.label}</span>
    </span>
  );
}

/**
 * Get configuration for status display
 */
function getStatusConfig(status: ObservationMatchStatus, confidence?: number) {
  switch (status) {
    case 'new':
      return {
        label: 'New',
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
        dotColor: 'bg-green-500',
        tooltip: 'This transaction will be imported as new',
      };

    case 'duplicate': {
      const confidencePercent = confidence ? Math.round(confidence * 100) : null;
      return {
        label: confidencePercent ? `Dup ${confidencePercent}%` : 'Duplicate',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-700',
        dotColor: 'bg-yellow-500',
        tooltip: confidencePercent
          ? `${confidencePercent}% match with existing transaction`
          : 'Possible duplicate of existing transaction',
      };
    }

    case 'needs_review':
      return {
        label: 'Review',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        dotColor: 'bg-red-500',
        tooltip: 'This transaction needs manual review',
      };

    default:
      return {
        label: 'Unknown',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-600',
        dotColor: 'bg-gray-400',
        tooltip: 'Status unknown',
      };
  }
}

export default StatusBadge;
